import React, { useEffect, useMemo, useState } from 'react';
import MarketingPublishingSettings from './MarketingPublishingSettings';
import PageHeader from './components/PageHeader';
import { apiFetch } from './utils/api';

const STATUS_LABELS = {
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
  wordpress_draft: 'WordPress draft',
  published: 'Published',
};

function statusClass(status) {
  if (status === 'published') return 'bg-green-100 text-green-800 border-green-200';
  if (status === 'approved') return 'bg-blue-100 text-blue-800 border-blue-200';
  if (status === 'awaiting_approval') return 'bg-amber-100 text-amber-800 border-amber-200';
  if (status === 'rejected') return 'bg-red-100 text-red-800 border-red-200';
  return 'bg-gray-100 text-gray-700 border-gray-200';
}

function formatDate(value) {
  if (!value) return 'Not set';
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseContent(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch (_) { return {}; }
}

function canDeleteBlogPost(post) {
  return post && !['approved', 'wordpress_draft', 'published'].includes(post.status);
}

function BlogPostsPage() {
  const [posts, setPosts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const selected = useMemo(
    () => posts.find(post => post.id === selectedId) || posts[0] || null,
    [posts, selectedId]
  );

  const structured = parseContent(selected?.structured_content);
  const previewHtml = selected?.approved_html_content || selected?.rendered_html_content || '';

  async function loadPosts(preferredId) {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/api/marketing/blog-posts');
      const data = await res.json();
      setPosts(data.posts || []);
      if (preferredId) {
        setSelectedId(preferredId);
      } else if (!selectedId && data.posts?.length) {
        setSelectedId(data.posts[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runAction(action, options = {}) {
    if (!selected) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await apiFetch(`/api/marketing/blog-posts/${selected.id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(options.body || {}),
      });
      const data = await res.json();
      const updated = data.post || data;
      await loadPosts(updated.id || selected.id);
      setMessage(options.success || 'Updated');
    } catch (err) {
      setError(err.message || `Failed to ${action}`);
    } finally {
      setBusy(false);
    }
  }

  async function deleteSelected() {
    if (!selected) return;
    if (!canDeleteBlogPost(selected)) {
      alert('Approved or published blog posts cannot be deleted.');
      return;
    }
    if (!window.confirm(`Delete blog post "${selected.title}"? This cannot be undone.`)) return;

    setBusy(true);
    setError('');
    setMessage('');
    try {
      const res = await apiFetch(`/api/marketing/blog-posts/${selected.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Delete failed');
        return;
      }
      setSelectedId(null);
      await loadPosts();
      setMessage('Blog post deleted.');
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Blog Posts"
        subtitle="Review Hermes-generated blog drafts before they go to WordPress"
        backTo="/marketing-hub"
        backLabel="Marketing Hub"
        sticky
      />

      <main className="max-w-7xl mx-auto px-6 py-8">
        <MarketingPublishingSettings />
        {error && (
          <div className="mb-4 border border-red-200 bg-red-50 text-red-700 rounded-lg px-4 py-3">
            {error}
          </div>
        )}
        {message && (
          <div className="mb-4 border border-green-200 bg-green-50 text-green-700 rounded-lg px-4 py-3">
            {message}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[360px,1fr] gap-6">
          <section className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Draft Queue</h2>
              <button
                type="button"
                onClick={() => loadPosts(selected?.id)}
                className="text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="p-6 text-gray-500">Loading blog posts...</div>
            ) : posts.length === 0 ? (
              <div className="p-6 text-gray-500">No blog drafts waiting yet.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {posts.map(post => (
                  <button
                    type="button"
                    key={post.id}
                    onClick={() => setSelectedId(post.id)}
                    className={`w-full text-left p-4 hover:bg-gray-50 ${selected?.id === post.id ? 'bg-blue-50' : 'bg-white'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-semibold text-gray-900 leading-snug">{post.title}</h3>
                      <span className={`shrink-0 text-xs px-2 py-1 rounded-full border ${statusClass(post.status)}`}>
                        {STATUS_LABELS[post.status] || post.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">{formatDate(post.updated_at)}</p>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            {!selected ? (
              <div className="p-8 text-gray-500">Select a blog draft to review.</div>
            ) : (
              <>
                <div className="p-5 border-b border-gray-200">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-1 rounded-full border ${statusClass(selected.status)}`}>
                          {STATUS_LABELS[selected.status] || selected.status}
                        </span>
                        <span className="text-xs text-gray-500">/{selected.slug}</span>
                      </div>
                      <h1 className="text-2xl font-bold text-gray-900">{selected.title}</h1>
                      {selected.excerpt && <p className="text-gray-600 mt-2">{selected.excerpt}</p>}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {selected.status === 'awaiting_approval' && (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => runAction('reject', { success: 'Blog post rejected.' })}
                            className="px-4 py-2 rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                          >
                            Reject
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => runAction('approve', { success: 'Blog post approved and locked.' })}
                            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            Approve
                          </button>
                        </>
                      )}
                      {selected.status === 'approved' && (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => runAction('publish', { body: { status: 'draft' }, success: 'Sent to WordPress as a draft.' })}
                            className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            Send to WordPress Draft
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => runAction('publish', { body: { status: 'publish' }, success: 'Published to WordPress.' })}
                            className="px-4 py-2 rounded-md border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50"
                          >
                            Publish Live
                          </button>
                        </>
                      )}
                      {canDeleteBlogPost(selected) && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={deleteSelected}
                          className="px-4 py-2 rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {selected.wordpress_url && (
                    <a
                      href={selected.wordpress_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-4 text-sm text-blue-700 hover:underline"
                    >
                      View WordPress post
                    </a>
                  )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-0">
                  <div className="p-6">
                    <h2 className="font-semibold text-gray-900 mb-4">Preview</h2>
                    <article
                      className="prose max-w-none border border-gray-200 rounded-lg p-6 bg-white"
                      dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                  </div>

                  <aside className="border-t lg:border-t-0 lg:border-l border-gray-200 p-6 bg-gray-50">
                    <h2 className="font-semibold text-gray-900 mb-4">Structured Content</h2>
                    <dl className="space-y-3 text-sm">
                      <div>
                        <dt className="text-gray-500">Category</dt>
                        <dd className="font-medium text-gray-900">{selected.category || 'general'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Meta description</dt>
                        <dd className="text-gray-800">{selected.meta_description || 'Not set'}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Featured image</dt>
                        <dd className="text-gray-800 break-words">
                          {structured.featured_image_url ? (
                            <a href={structured.featured_image_url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                              {structured.featured_image_alt || 'Selected image'}
                            </a>
                          ) : 'Not set'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Created</dt>
                        <dd className="text-gray-800">{formatDate(selected.created_at)}</dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Approved</dt>
                        <dd className="text-gray-800">{formatDate(selected.approved_at)}</dd>
                      </div>
                    </dl>

                    <div className="mt-6">
                      <h3 className="font-medium text-gray-900 mb-2">Sections</h3>
                      <div className="space-y-3">
                        {(structured.sections || []).map((section, index) => (
                          <div key={`${section.heading}-${index}`} className="border border-gray-200 rounded-lg bg-white p-3">
                            <p className="font-medium text-gray-900">{section.heading || `Section ${index + 1}`}</p>
                            <p className="text-xs text-gray-500 mt-1">{String(section.body || '').slice(0, 140)}{String(section.body || '').length > 140 ? '...' : ''}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </aside>
                </div>
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}

export default BlogPostsPage;
